import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { FieldType } from '@ngx-formly/material/form-field';
import { cloneDeep, get, isArray, isEmpty, isEqual, set, sortBy } from 'lodash';
import { Subscription } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';

export interface IMultiSelectList {
  label: string;
  value: string;
  checked?: boolean; // used only for hierarchical list
  items: Array<IMultiSelectList>;
  level?: number;
}

@Component({
  selector: 'app-multi-select-list',
  templateUrl: './multi-select-list.component.html',
  styleUrls: ['./multi-select-list.component.scss'],
})
export class FormlyFieldMultiSelectListComponent
  extends FieldType
  implements OnInit, OnDestroy
{
  selectedChange: EventEmitter<Array<string>> = new EventEmitter<
    Array<string>
  >();
  selectedValue = [];
  listItem: Array<IMultiSelectList> = [];
  subscription = new Subscription();
  isHierarchicalList: boolean = false; // to check if given list is hierarchical or not
  topHierarchicalListItem: Array<IMultiSelectList> = []; // store top items of the hierarchical list to be displayed
  originalListItem: Array<IMultiSelectList> = []; // back up of List items. This is used to maintain the original order of displayed list items.
  previousSelectedValue = []; // holds the previous value of selectedValue
  callDisplaySelectedItemsOnTopFn: boolean = true; // boolean variable to check if the displaySelectedItemsOnTop method should run or not
  isMultiSelect: Boolean = false;
  @ViewChild(MatSelect, { static: true }) mySelect: MatSelect;

  constructor() {
    super();
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    // if form is public or contact self service registration, then on load displaySelectedItemsOnTop should not be called
    const pathName = this.getPathName();
    if (pathName?.indexOf('contact-form/update') < 0) {
      this.callDisplaySelectedItemsOnTopFn = false;
    }
    this.subscription.add(
      this.formControl.valueChanges.subscribe((value) => {
        if (!value) {
          this.selectedValue = [];
        } else {
          this.selectedValue = value;
          if (this.callDisplaySelectedItemsOnTopFn) {
            // This will be called on load only if form is an update form
            this.displaySelectedItemsOnTop();
          }
        }
      })
    );
    this.setDefaultValue();
  }

  onSelectedChange(event) {
    this.callDisplaySelectedItemsOnTopFn = false;
    this.formControl.setValue(event.value);
    this.selectedChange.emit(event.value);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    // if it is multi-select and hierarchical list, make sure top items are in sync with selectedValue
    if (this.isMultiSelect && this.isHierarchicalList) {
      this.topHierarchicalListItem = this.topHierarchicalListItem?.map(
        (item) => {
          if (event?.value?.includes(item?.value)) {
            item.checked = true;
          } else {
            item.checked = false;
          }
          return item;
        }
      );
    }
  }

  private setDefaultValue() {
    const defaultValue = get(this, 'field.templateOptions.defaultValue', null);
    const listItemsSelect = get(this, 'field.templateOptions.listItems', []);
    this.isMultiSelect = get(
      this,
      'field.templateOptions.isMultiSelect',
      false
    );
    this.listItem = Array.isArray(listItemsSelect)
      ? listItemsSelect
      : [listItemsSelect];
    this.rebuildListItems(0, this.listItem);
    this.originalListItem = cloneDeep(this.listItem);
    if (defaultValue) {
      this.formControl.setValue(defaultValue);
      this.selectedValue = defaultValue;
      this.displaySelectedItemsOnTop();
    }
  }

  private rebuildListItems(
    index: number = 0,
    listItem: Array<IMultiSelectList>
  ) {
    listItem.forEach((item) => {
      item.level = index;
      if (!isEmpty(item.items)) {
        this.rebuildListItems(index + 1, item.items);
      }
    });
  }

  checkIfDropdownWasToggled(isOpened: boolean): void {
    // if dropdown is closed, modify the list to display selected items on the top
    if (!isOpened) {
      if (
        !isEqual(sortBy(this.previousSelectedValue), sortBy(this.selectedValue))
      ) {
        this.displaySelectedItemsOnTop();
      }
    } else if (isOpened) {
      set(this.mySelect, 'panel.nativeElement.scrollTop', 0);
    }
  }

  displaySelectedItemsOnTop(): void {
    let _selectedValue = cloneDeep(this.selectedValue);
    this.previousSelectedValue = cloneDeep(this.selectedValue);
    let listItem: IMultiSelectList[] = cloneDeep(this.originalListItem);
    if (!isArray(_selectedValue)) {
      _selectedValue = [_selectedValue];
    }
    if (get(_selectedValue, 'length', 0) > 0) {
      this.getSelectedItemsFromListItem(listItem, _selectedValue);
    } else {
      this.listItem = cloneDeep(this.originalListItem);
      this.topHierarchicalListItem = [];
    }
  }

  getSelectedItemsFromListItem(
    listItem: Array<IMultiSelectList>,
    _selectedValue: any[]
  ): void {
    const filteredItems: IMultiSelectList[] = [];
    const itemsOnTop: IMultiSelectList[] = [];
    for (let i = 0; i < listItem?.length; i++) {
      // if list item has further children, then it is an hierarchical list
      if (isEmpty(listItem[i]?.items)) {
        if (!_selectedValue?.includes(listItem[i]?.value)) {
          filteredItems.push(listItem[i]);
        } else {
          itemsOnTop.push(listItem[i]);
        }
      } else {
        this.isHierarchicalList = true;
        break;
      }
    }
    //if it is hierarchical list, do not modify the listItem
    if (!this.isHierarchicalList) {
      listItem = [...itemsOnTop, ...filteredItems];
      this.listItem = cloneDeep(listItem);
    } else {
      //if it is hierarchical list, create another list to be displayed on top
      this.topHierarchicalListItem = [];
      this.getTopItemsForHierarchicalList(_selectedValue, listItem);
    }
    this.callDisplaySelectedItemsOnTopFn = false;
  }

  getTopItemsForHierarchicalList(
    _selectedValue: any[],
    listItem: IMultiSelectList[]
  ): void {
    const traverseHierarchicalList = (listItem) => {
      listItem.forEach((item) => {
        if (_selectedValue.includes(item.value)) {
          set(item, 'checked', true);
          this.topHierarchicalListItem.push(item);
        }
        if (!isEmpty(item.items)) {
          traverseHierarchicalList(item.items);
        }
      });
    };
    traverseHierarchicalList(listItem);
  }

  // To handle actions performed on top items of multi-select hierarchical list
  getSelectedValueFromHierarchicalTopItems(
    item: IMultiSelectList,
    event
  ): void {
    // make sure any modifications done to hierarchical top items, needs to be stored back in selectedValue for multi-select
    if (this.isMultiSelect) {
      // if item is unchecked, remove it from array
      if (!event.checked) {
        {
          this.selectedValue = this.selectedValue?.filter(
            (value) => value !== item?.value
          );
        }
      } else {
        // if item is checked back again, add it in array
        this.selectedValue = [...this.selectedValue, item?.value];
      }
      item.checked = event.checked; // finally set the checkbox value of top hierarchical actioned item
    }
  }

  getPathName(): any {
    return get(window, 'location.pathname', null);
  }
}
